import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageSquare,
  Filter as FilterIcon,
  Flag,
  Clock,
  UserCog,
  AlarmClock,
  Calendar,
  Copy,
  MoreVertical,
  Send,
  Phone,
  Eye,
} from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import { JourneyCard } from "./profile-journey-cards";
import { LeadRecord, StatusPill, ChannelLabel, formatSourceLabel, goToLeadConversation } from "../../marketing-pages/leads/shared";

// ─────────────────────────────────────────────────────────────
// conversation_agent's Home — built against
// crm-agent-home-dashboard-ui-integration.md. One request
// (GET /dashboard/agent-home) backs the whole screen; per that doc, sections
// with no backend source yet (attention tasks, pending follow-ups, overdue
// actions, global recent activity, CRM onboarding progress) render an honest
// "Not available yet" state instead of a fabricated 0/empty list — see the
// doc's "Known backend gaps" section for why each one is unavailable.
//
// The org profile card (name/domain/about/logo only — no phone, industry, or
// country; the doc is explicit that those aren't in this payload) comes from
// this same response's `organization` field rather than a second
// /auth/get_userdata call, matching the doc's "one authenticated request"
// guidance for the initial render.
// ─────────────────────────────────────────────────────────────

type PerformancePeriod = "day" | "week" | "month";
type Channel = "whatsapp" | "rcs" | "email";
type CallStatus = "scheduled" | "completed" | "cancelled";

interface AgentIdentity {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string | null;
}

interface Organization {
  id: string;
  name: string;
  domain_name: string | null;
  about: string | null;
  logo: string | null;
  is_active: boolean;
}

interface AvailabilityMetric {
  available: boolean;
  value: number | null;
}

interface UnavailableCollection {
  available: boolean;
  items: Record<string, unknown>[];
}

interface ActivityActor {
  user_id: string | null;
  name: string | null;
  email: string | null;
}

interface ConversationCall {
  id: string;
  conversation_id: string;
  status: CallStatus;
  scheduled_at: string;
  time_zone: string;
  purpose: string | null;
  assigned_to: ActivityActor;
}

interface RecentConversation {
  id: string;
  title: string | null;
  channel: Channel | null;
  last_message_at: string | null;
  last_message: { body: string | null; direction: string } | null;
  deal_stage: "new_lead" | "contacted" | "proposal_shared" | "deal_won";
}

interface AssignedLead {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  display_name: string | null;
  conversation_id: string | null;
  external_thread_id: string | null;
  source: string | null;
  score: number;
  status: string;
  tags: string[];
  custom: { assigned_to?: string; channel?: string; [key: string]: unknown };
  updated_at: string | null;
  temperature: "hot" | "warm" | "cold";
}

interface AgentHomeDashboard {
  generated_at: string;
  agent: AgentIdentity;
  organization: Organization | null;
  performance: {
    conversations_handled: number;
    leads_in_pipeline: number;
    deals_won: number;
    avg_response_seconds: number | null;
    leads_contacted: number;
    conversion_rate_percent: number | null;
  };
  glance: {
    calls_scheduled_today: number;
    missed_conversations: number;
    pending_follow_ups: AvailabilityMetric;
    overdue_actions: AvailabilityMetric;
  };
  attention_tasks: UnavailableCollection;
  calls_today: ConversationCall[];
  recent_conversations: RecentConversation[];
  recent_activity: UnavailableCollection;
  recently_assigned_leads: AssignedLead[];
}

// ── Formatting helpers (per the doc's "Formatting helpers" section) ──
const formatDuration = (totalSeconds: number | null): string => {
  if (totalSeconds == null) return "—";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
};

const formatCallTime = (call: ConversationCall): string =>
  new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: call.time_zone }).format(
    new Date(call.scheduled_at),
  );

const agentDisplayName = (agent: AgentIdentity): string => {
  const name = [agent.first_name, agent.last_name].filter(Boolean).join(" ").trim();
  return name || agent.email || "there";
};

const leadDisplayName = (lead: AssignedLead): string =>
  lead.display_name ||
  [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() ||
  lead.email ||
  lead.phone ||
  "Unknown lead";

const leadInitials = (name: string): string =>
  name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

// Reuses goToLeadConversation's existing conversation-id-first / phone-search
// fallback logic (pages/leads/shared.tsx) rather than duplicating it — this
// just repackages the aggregator's AssignedLead into the LeadRecord shape
// that helper (and the row UI below) already expects.
const assignedLeadToLeadRecord = (lead: AssignedLead): LeadRecord => {
  const name = leadDisplayName(lead);
  return {
    id: lead.id,
    name,
    display_name: name,
    initials: leadInitials(name),
    phone: lead.phone,
    email: lead.email,
    tags: lead.tags || [],
    status: lead.status,
    channel: (lead.custom?.channel as string) || null,
    temperature: lead.temperature,
    score: lead.score,
    updatedAt: lead.updated_at,
    source: lead.source,
    assignedTo: (lead.custom?.assigned_to as string) || null,
    raw: {
      id: lead.id,
      resource_type: "lead",
      email: lead.email,
      phone: lead.phone,
      first_name: lead.first_name,
      display_name: lead.display_name,
      last_name: lead.last_name,
      status: lead.status,
      updated_at: lead.updated_at || "",
      details: {
        source: lead.source || undefined,
        score: lead.score,
        tags: lead.tags,
        custom: lead.custom,
        conversation_id: lead.conversation_id,
        external_thread_id: lead.external_thread_id || undefined,
      },
    },
  };
};

// Local calendar date in the given IANA zone, formatted YYYY-MM-DD — not
// `toISOString().slice(0, 10)`, which the doc explicitly warns can shift the
// date backward/forward depending on the viewer's zone vs UTC.
const localDateString = (timeZone: string): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

// Unlike /auth/get_userdata's `org_logo` (already a full URL), this
// aggregator's `organization.logo` comes back as a bare relative media path
// (confirmed against a real response: "dev/<org>/<date>/<file>.jpg") — needs
// the media host prefixed the same way org_logo already has it baked in.
const resolveOrgLogoUrl = (logo: string | null): string | null => {
  if (!logo) return null;
  if (/^https?:\/\//i.test(logo)) return logo;
  const mediaBase = process.env.REACT_APP_BACKEND_URL || "https://api.connect.dalmlabs.com";
  return `${mediaBase}/media/${logo.replace(/^\/+/, "")}`;
};

const StatTile: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub: string }> = ({
  icon, label, value, sub,
}) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
    <div className="flex items-center gap-2 mb-2 text-gray-500 dark:text-gray-400 text-xs font-medium">
      {icon}
      <span className="text-[#3A3428] dark:text-gray-100 font-medium text-sm">{label}</span>
    </div>
    <p className="text-2xl font-semibold text-[#434343] dark:text-gray-200">{value}</p>
    <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1 leading-relaxed">{sub}</p>
  </div>
);

const EmptyListState: React.FC<{ label: string }> = ({ label }) => (
  <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500 py-6 text-center">{label}</p>
);

// Distinct from EmptyListState on purpose — "genuinely nothing here" and
// "backend doesn't support this yet" are different facts and the doc is
// explicit they must not be conflated (attention_tasks/recent_activity are
// always `available: false` today, not empty).
const UnavailableState: React.FC = () => (
  <p className="text-sm text-gray-300 dark:text-gray-600 py-6 text-center italic">Not available yet</p>
);

const openConversation = (navigate: ReturnType<typeof useNavigate>, conversationId: string, title?: string | null) => {
  sessionStorage.setItem("pendingConversationTarget", JSON.stringify({ conversationId, title: title || undefined }));
  navigate("/chats");
};

const LeadAssignedRow: React.FC<{ lead: LeadRecord }> = ({ lead }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const sourceLabel = formatSourceLabel(lead.source);

  const handleCopyPhone = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!lead.phone) return;
    navigator.clipboard.writeText(lead.phone);
    toast.success("Phone number copied");
  };

  const handleCallContact = () => {
    setMenuOpen(false);
    if (!lead.phone) {
      toast.error("This lead has no phone number on file yet.");
      return;
    }
    window.location.href = `tel:${lead.phone}`;
  };

  return (
    <div className="flex items-center justify-between gap-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded-lg px-1 -mx-1">
      <button
        onClick={() => goToLeadConversation(navigate, lead)}
        className="flex items-center gap-2.5 min-w-0 flex-1 bg-transparent border-none cursor-pointer p-0 text-left"
      >
        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-300 text-xs font-semibold shrink-0">
          {lead.initials}
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{lead.display_name}</p>
      </button>

      <div className="hidden sm:block w-24 shrink-0">
        {sourceLabel ? (
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 capitalize truncate">{sourceLabel}</span>
        ) : (
          <ChannelLabel channel={lead.channel} />
        )}
      </div>

      <div className="hidden sm:flex items-center gap-1.5 w-36 shrink-0 text-xs text-gray-500 dark:text-gray-400">
        <span className="truncate">{lead.phone || "No phone on file"}</span>
        {lead.phone && (
          <button
            onClick={handleCopyPhone}
            title="Copy phone number"
            className="text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 bg-transparent border-none cursor-pointer p-0 shrink-0"
          >
            <Copy size={12} />
          </button>
        )}
      </div>

      <StatusPill status={lead.status} />

      <div className="relative shrink-0">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-9 z-20 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden py-1">
              <button
                onClick={() => { setMenuOpen(false); goToLeadConversation(navigate, lead); }}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer"
              >
                <Send size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                Send Message
              </button>
              <button
                onClick={handleCallContact}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer"
              >
                <Phone size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                Call Contact
              </button>
              <Link
                to="/leads"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 no-underline"
              >
                <Eye size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                View Lead Details
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// FastAPI's error shape differs by status: 401/403/5xx usually send
// `{ detail: "some string" }`, but 422s send `{ detail: [{ type, loc, msg, ... }] }`
// — an array of validation-error objects. Rendering `detail` straight into
// JSX crashes React ("Objects are not valid as a React child") the moment a
// 422 comes back, so this always reduces it to a plain string first.
const extractErrorMessage = (response: any, fallback: string): string => {
  const detail = response?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ") || fallback;
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return fallback;
};

// A failed/not-yet-deployed aggregator must never blank the whole screen —
// this is what renders instead: every widget in its normal "unavailable/
// empty" state rather than a fabricated number, so the page still looks and
// works like a dashboard. Real name/email come from the cached login blob
// (same file-local-JSON-parse pattern used elsewhere, e.g.
// update-customer.tsx's getStoredUserDefaults) since even that much is
// better than a blank greeting.
const emptyDashboard = (): AgentHomeDashboard => {
  let agent: AgentIdentity = { id: "", first_name: null, last_name: null, email: "", role: null };
  try {
    const stored = localStorage.getItem("userDetails");
    if (stored) {
      const parsed = JSON.parse(stored);
      agent = {
        id: parsed?.user_id || "",
        first_name: parsed?.first_name || null,
        last_name: parsed?.last_name || null,
        email: parsed?.email || "",
        role: parsed?.org_role || parsed?.role || null,
      };
    }
  } catch {
    // no cached identity — greeting falls back to "there" below.
  }
  return {
    generated_at: new Date().toISOString(),
    agent,
    organization: null,
    performance: {
      conversations_handled: 0,
      leads_in_pipeline: 0,
      deals_won: 0,
      avg_response_seconds: null,
      leads_contacted: 0,
      conversion_rate_percent: null,
    },
    glance: {
      calls_scheduled_today: 0,
      missed_conversations: 0,
      pending_follow_ups: { available: false, value: null },
      overdue_actions: { available: false, value: null },
    },
    attention_tasks: { available: false, items: [] },
    calls_today: [],
    recent_conversations: [],
    recent_activity: { available: false, items: [] },
    recently_assigned_leads: [],
  };
};

const ConversationAgentHome: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [dashboard, setDashboard] = useState<AgentHomeDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  // Stable for the browser session — computed once and sent as a header
  // (X-Time-Zone) rather than a query param, per request (2026-08-18).
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { makeRequest: getAgentHome } = useCrmApi("/dashboard/agent-home", "GET", {
    Authorization: `Bearer ${token}`,
    "X-Time-Zone": timeZone,
  });

  const load = async () => {
    setLoading(true);
    const [response, status] = await getAgentHome(undefined, {
      date: localDateString(timeZone),
      performance_period: "month" as PerformancePeriod,
      recent_limit: 4,
    });
    if (status >= 200 && status < 300 && response) {
      setDashboard(response);
    } else {
      // Non-blocking — a failed aggregator call degrades the dashboard to
      // its empty/unavailable state instead of replacing the whole screen
      // with an error. Only surfaced as a toast, and only on the first
      // load; a background 60s refresh failing silently keeps whatever
      // data is already on screen instead of re-toasting every minute.
      if (!dashboard) {
        setDashboard(emptyDashboard());
        toast.error(extractErrorMessage(response, "Couldn't load dashboard data"));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Per the doc: refetch on window focus and every 60s while visible.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const interval = window.setInterval(load, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !dashboard) {
    return <div className="py-12 text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</div>;
  }

  if (!dashboard) return null;

  const { agent, organization, performance, glance, attention_tasks, calls_today, recent_conversations, recent_activity, recently_assigned_leads } = dashboard;
  const assignedLeadRecords = recently_assigned_leads.map(assignedLeadToLeadRecord);
  const hasMoreCallsToday = calls_today.length < glance.calls_scheduled_today;

  return (
    <div className="min-h-screen">
      <div className="mx-auto space-y-6">
        <h1 className="text-[24px] text-gray-900 dark:text-gray-100">
          Hi, <span className="font-bold">{agentDisplayName(agent)}</span>
        </h1>
        <p className="text-[14px] font-semibold mb-4 text-gray-700 dark:text-gray-300">
          Let's pick up where you left off and keep things moving.
        </p>

        {/* Org card (name/domain/about/logo only — this payload has nothing
            more) + the static setup checklist. No QR-lead-capture widget. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {organization && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-3">
                {resolveOrgLogoUrl(organization.logo) && (
                  <img src={resolveOrgLogoUrl(organization.logo)!} alt={organization.name} className="w-6 h-6 rounded object-cover" />
                )}
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{organization.name}</span>
              </div>
              {organization.domain_name && (
                <a
                  href={`https://${organization.domain_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[14px] border-t border-b border-gray-200 dark:border-gray-700 p-1 block text-[#4338CA] underline"
                >
                  {organization.domain_name}
                </a>
              )}
              <p className="text-[14px] text-[#434343] dark:text-gray-300 border-t border-b border-gray-200 dark:border-gray-700 p-1">
                {organization.about || "N/A"}
              </p>
            </div>
          )}
          <JourneyCard />
        </div>

        {/* My Performance */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">My Performance</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatTile icon={<MessageSquare size={16} />} label="Total Conversations Handled" value={performance.conversations_handled} sub="Unique conversations handled by you" />
            <StatTile icon={<FilterIcon size={16} />} label="Total Leads in Pipeline" value={performance.leads_in_pipeline} sub="Leads currently assigned to you" />
            <StatTile icon={<Flag size={16} />} label="Deals Won" value={performance.deals_won} sub="Deals that reached closed stage" />
            <StatTile icon={<Clock size={16} />} label="Avg. Response Time" value={formatDuration(performance.avg_response_seconds)} sub="Average response time since first message from contact" />
            <StatTile
              icon={<UserCog size={16} />}
              label="Conversion Rate"
              value={performance.conversion_rate_percent != null ? `${performance.conversion_rate_percent}%` : "—"}
              sub="Your Contact to Deals closure rate"
            />
          </div>
        </section>

        {/* Numbers at a Glance */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Numbers at a Glance</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile icon={<Calendar size={16} />} label="Calls Scheduled Today" value={glance.calls_scheduled_today} sub="Calls scheduled for today" />
            <StatTile icon={<MessageSquare size={16} />} label="Missed Conversations" value={glance.missed_conversations} sub="Conversations missed while you were away" />
            <StatTile
              icon={<UserCog size={16} />}
              label="Follow-ups"
              value={glance.pending_follow_ups.available ? glance.pending_follow_ups.value : "—"}
              sub={glance.pending_follow_ups.available ? "Pending follow-ups on contacts" : "Not available yet"}
            />
            <StatTile
              icon={<AlarmClock size={16} />}
              label="Actions Overdue"
              value={glance.overdue_actions.available ? glance.overdue_actions.value : "—"}
              sub={glance.overdue_actions.available ? "Follow-ups that are past due" : "Not available yet"}
            />
          </div>
        </section>

        {/* Keep up with conversations and calls */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Keep up with conversations and calls</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100">
                  <AlarmClock size={18} /> Tasks Needing your Attention
                </h3>
              </div>
              {attention_tasks.available ? (
                attention_tasks.items.length === 0 ? (
                  <EmptyListState label="Nothing needs your attention right now." />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{attention_tasks.items.length} task(s)</p>
                )
              ) : (
                <UnavailableState />
              )}
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100">
                  <Calendar size={18} /> Calls Scheduled Today
                </h3>
                <span className="text-[14px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 w-7 h-7 flex items-center justify-center rounded-lg font-bold">
                  {glance.calls_scheduled_today}
                </span>
              </div>
              {calls_today.length === 0 ? (
                <EmptyListState label="No calls scheduled for today." />
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {calls_today.map((call) => (
                    <button
                      key={call.id}
                      onClick={() => openConversation(navigate, call.conversation_id)}
                      className="w-full flex items-center justify-between py-2.5 text-left bg-transparent border-none cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded-lg px-1 -mx-1"
                    >
                      <span className="text-sm text-gray-800 dark:text-gray-200">{formatCallTime(call)}</span>
                      <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 capitalize">{call.purpose?.replace(/_/g, " ") || call.status}</span>
                    </button>
                  ))}
                </div>
              )}
              {hasMoreCallsToday && (
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 pt-2">
                  +{glance.calls_scheduled_today - calls_today.length} more scheduled today
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Ongoing Activity */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Ongoing Activity</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100 mb-3">
                <MessageSquare size={18} /> Recently Ongoing Conversations
              </h3>
              {recent_conversations.length === 0 ? (
                <EmptyListState label="No ongoing conversations yet." />
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {recent_conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => openConversation(navigate, conv.id, conv.title)}
                      className="w-full flex items-center justify-between gap-2 py-2.5 text-left bg-transparent border-none cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded-lg px-1 -mx-1"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{conv.title || "Untitled"}</p>
                        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{conv.last_message?.body || "No messages yet"}</p>
                      </div>
                      {conv.channel && <ChannelLabel channel={conv.channel} />}
                    </button>
                  ))}
                </div>
              )}
              <div className="pt-3">
                <Link to="/chats" className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                  View All Conversations
                </Link>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
              <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100 mb-3">
                <Clock size={18} /> Your recent activity
              </h3>
              {recent_activity.available ? (
                <EmptyListState label="Nothing to show yet." />
              ) : (
                <UnavailableState />
              )}
            </div>
          </div>
        </section>

        {/* Leads Assigned */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Leads Assigned</h2>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] flex gap-2 font-semibold text-[#3A3428] dark:text-gray-100">
                <FilterIcon size={18} /> Leads recently assigned to you
              </h3>
              <span className="text-[14px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 w-7 h-7 flex items-center justify-center rounded-lg font-bold">
                {assignedLeadRecords.length}
              </span>
            </div>
            {assignedLeadRecords.length === 0 ? (
              <EmptyListState label="No leads assigned to you yet." />
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-800">
                {assignedLeadRecords.map((lead) => (
                  <LeadAssignedRow key={lead.id} lead={lead} />
                ))}
              </div>
            )}
            <div className="pt-3">
              <Link to="/leads" className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                View All Assigned
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConversationAgentHome;
